import type { Chapter, ValidationResult } from '../types';
import { FOUR_BEAT_V1_PROVENANCE } from '../types';
import type { FourBeatChapterSession } from './fourBeatInspectorOrchestration.service';
import { FOUR_BEAT_SEPARATOR } from './fourBeatChapterAssembly.service';

let activeFourBeatSession: FourBeatChapterSession | null = null;

export function registerFourBeatChapterSession(session: FourBeatChapterSession): void {
  activeFourBeatSession = session;
}

export function getActiveFourBeatChapterSession(): FourBeatChapterSession | null {
  return activeFourBeatSession;
}

export function clearFourBeatChapterSession(reason: string, inspectorCallsUsed?: number): void {
  if (!activeFourBeatSession) return;
  const reportedInspectorCalls =
    typeof inspectorCallsUsed === 'number'
      ? inspectorCallsUsed
      : activeFourBeatSession.inspectorCallsUsed;
  console.log(
    `[FOUR BEAT SESSION CLEARED] ${JSON.stringify({
      reason,
      projectId: activeFourBeatSession.projectId,
      chapterNumber: activeFourBeatSession.chapterNumber,
      runId: activeFourBeatSession.runId,
      logicalCallsUsed: activeFourBeatSession.callBudget.logicalCallsUsed,
      inspectorCallsUsed: reportedInspectorCalls,
    })}`,
  );
  activeFourBeatSession = null;
}

export interface FourBeatSessionScope {
  projectId: string;
  chapterNumber: number;
  runId: string;
}

export function validateFourBeatSessionScope(
  session: FourBeatChapterSession | null,
  scope: FourBeatSessionScope,
): FourBeatChapterSession | null {
  const candidate = session ?? activeFourBeatSession;
  if (!candidate) return null;

  const mismatch =
    candidate.projectId !== scope.projectId ||
    candidate.chapterNumber !== scope.chapterNumber ||
    candidate.runId !== scope.runId;

  if (mismatch) {
    console.warn(
      `[FOUR BEAT SESSION REJECTED] ${JSON.stringify({
        reason: 'identity_mismatch',
        expected: scope,
        received: {
          projectId: candidate.projectId,
          chapterNumber: candidate.chapterNumber,
          runId: candidate.runId,
        },
      })}`,
    );
    return null;
  }

  return candidate;
}

/** Content separator check for assembly/parsing only — not authoritative four-beat provenance. */
export function chapterContentHasFourBeatSeparator(content?: string): boolean {
  return Boolean(content?.includes(FOUR_BEAT_SEPARATOR));
}

/** @deprecated Use chapterContentHasFourBeatSeparator — separators are not provenance. */
export function isFourBeatV1ChapterContent(content?: string): boolean {
  return chapterContentHasFourBeatSeparator(content);
}

export function hasFourBeatV1Provenance(chapter: Chapter | null | undefined): boolean {
  if (!chapter?.fourBeatProvenance) return false;
  return (
    chapter.fourBeatProvenance.responseFormat === FOUR_BEAT_V1_PROVENANCE.responseFormat &&
    chapter.fourBeatProvenance.generationArchitecture === FOUR_BEAT_V1_PROVENANCE.generationArchitecture
  );
}

/**
 * Current-generation four-beat orchestration is identified only by a scoped session
 * or explicit generator provenance — never by manuscript separators alone.
 */
export function isFourBeatV1Orchestration(params: {
  session: FourBeatChapterSession | null;
  chapter: Chapter | null;
}): boolean {
  if (params.session) return true;
  return hasFourBeatV1Provenance(params.chapter);
}

/** Legacy full-chapter Inspector correction loop applies only outside FOUR_BEAT_V1 orchestration. */
export function shouldUseLegacyInspectorCorrectionLoop(params: {
  session: FourBeatChapterSession | null;
  chapter: Chapter | null;
}): boolean {
  return !isFourBeatV1Orchestration(params);
}

export function isFourBeatInspectorPass(validation: ValidationResult): boolean {
  return validation.verdict === 'PASS' || validation.status === 'PASS';
}

export function shouldTerminateFourBeatInspectorPath(
  validation: ValidationResult,
  inspectorCallsUsed: number,
): boolean {
  if (isFourBeatInspectorPass(validation)) return false;
  if (inspectorCallsUsed >= 1) return true;
  if (validation.verdict === 'BLOCKED') return true;
  if (validation.verdict === 'REPAIR_REQUIRED') return true;
  return validation.status === 'FAIL';
}
